import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Icon } from "rhombus-react";

import { noop, isDynamic } from "utils/common";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { getManualClassificationEnabled } from "selectors/Intelligence";

import BladeActions from "widgets/BladeActions";
import { VIDEO_AD_TYPES } from "utils/constants";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";
import {
	PREDICTIVE_SCORE_ACTION,
	useExclusivePanelToggle,
	SHOW_LABELS_PARAM,
	SHOW_SCORE_PARAM
} from "widgets/modals/creatives/preview/PanelActions";

const SHOW_LABELS_ACTION = {
	shouldDisplay({ isManualClassificationEnabled }){
		return isManualClassificationEnabled;
	},
	component: () => {
		const isCreativeDynamic = useSelector(state => isDynamic(state.creativeDetails));
		const { isVisible: showLabels, toggle } = useExclusivePanelToggle(SHOW_LABELS_PARAM, SHOW_SCORE_PARAM);

		if(showLabels){
			return (
				<div onClick={toggle}>
					<Icon
						name="fas fa-tag active"
						data-tip="Creative Labels"
						data-place="left"
					/>
				</div>
			);
		}

		if(isCreativeDynamic){
			return (
				<Icon
					name="far fa-tag disabled"
					data-tip="Creative labels must be applied to versions for dynamic creatives."
					data-place="left"
				/>
			);
		}
		return (
			<div onClick={toggle}>
				<Icon
					name="far fa-tag"
					data-tip="Creative Labels"
					data-place="left"
				/>
			</div>
		);
	},
};

const REPLAY_ACTION = {
	shouldDisplay: context => context.onReplay,
	component: (context) => {
		return (
			<div
				onClick={context.onReplay || noop}
			>
				<Icon name="far fa-sync-alt" data-tip="Replay" data-place="left" />
			</div>
		);
	},
};

const EXTERNAL_PREVIEW_ACTION = {
	shouldDisplay: context => context.previewURL,
	component: (context) => {
		const isLoaded = useSelector(state => state.creativeDetails.isLoaded);
		if(isLoaded){
			return (
				<a href={context.previewURL} target="_blank">
					<Icon
						name="far fa-external-link-alt"
						data-tip="Preview Creative"
						data-place="left"
					/>
				</a>
			);
		}
		return (
			<Icon
				name="far fa-external-link-alt disabled"
				data-tip="Preview Creative"
				data-place="left"
			/>
		);
	},
};

const MUTE_ACTION = {
	shouldDisplay: (context) => {
		return VIDEO_AD_TYPES.includes(context?.adType) || context.isCarouselVideo;
	},
	component: (context) => {
		const { muted, setIsMuted } = context;
		if (muted) {
			return (
				<div onClick={() => setIsMuted(false)}>
					<Icon
						name="far fa-volume-mute"
						data-tip="Unmute"
						data-place="left"
					/>
				</div>
			);
		}

		return (
			<div onClick={() => setIsMuted(true)}>
				<Icon
					name="far fa-volume"
					data-tip="Mute"
					data-place="left"
				/>
			</div>
		);
	},
};

export default function CreativePreviewBladeToolbar(props){
	useTooltipRebuild();
	const isManualClassificationEnabled = useSelector(getManualClassificationEnabled);
	const isPredictiveScoringOn = usePredictiveScoringEnabled();
	// The basic preview blade doesn't pass adType as a prop (only the social blade does), so read it
	// from the store — the score action gates on it, and it must be present in both blades.
	const creativeAdType = useSelector(state => state.creativeDetails?.adType);
	const actionProps = {
		actionLists: [
			[SHOW_LABELS_ACTION, PREDICTIVE_SCORE_ACTION],
			[REPLAY_ACTION],
			[EXTERNAL_PREVIEW_ACTION, MUTE_ACTION],
		],
		actionContext: {
			isManualClassificationEnabled,
			isPredictiveScoringOn,
			adType: creativeAdType,
			...props,
		},
	};

	return <BladeActions {...actionProps} />;
}

CreativePreviewBladeToolbar.displayName = "CreativePreviewBladeToolbar";
CreativePreviewBladeToolbar.propTypes = {
	previewURL: PropTypes.string,
	onReplay: PropTypes.func,
};
